import { Routes } from '@angular/router';
import { authGuard } from './shared/guards/auth.guard';
import { roleGuard } from './shared/guards/role.guard';
import { internalUserGuard } from './shared/guards/internal-user.guard';
import { tenantOptionGuard } from './shared/guards/tenant-option.guard';
import { Roles } from './shared/models/roles';
import { TenantOptions } from './shared/models/tenant-options';
import { MAINTENANCE_ENABLED } from './shared/constants/maintenance.constants';
import { initialRedirect, withAppGuard } from './shared/app-ui/utils/with-app-guard';
import { appOnlyGuard } from './shared/app-ui/guards/app-only.guard';
import {
  CREDIT_OVERVIEW_ALLOWED_ROLES,
  PROPOSAL_ALLOWED_ROLES,
} from './shared/components/menu/constants/menu.constants';

const APP_ROUTES: Routes = [
  {
    path: '',
    redirectTo: initialRedirect('cliente', 'analise-de-credito'),
    pathMatch: 'full',
  },
  {
    path: '',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
    data: { appEnabled: true },
  },
  {
    path: 'app',
    loadChildren: () => import('./shared/app-ui/app-ui.routes').then((m) => m.APP_UI_ROUTES),
    canActivate: [authGuard],
    data: { appEnabled: true },
  },
  {
    path: 'gerencial',
    loadChildren: () => import('./credit-overview/credit-overview.routes').then((m) => m.CREDIT_OVERVIEW_ROUTES),
    canActivate: [authGuard, roleGuard],
    data: {
      requiredRoles: CREDIT_OVERVIEW_ALLOWED_ROLES,
      requiredRolesMode: 'any',
    },
    title: 'Gerencial',
  },
  {
    path: 'painel-indicadores',
    loadChildren: () => import('./indicators/indicators.routes').then((m) => m.INDICATORS_ROUTES),
    canActivate: [authGuard, roleGuard],
    data: {
      requiredRoles: [Roles.ANALYST],
      requiredRolesMode: 'any',
    },
    title: 'Painel de Indicadores',
  },
  {
    path: 'analise-de-credito',
    loadChildren: () => import('./credit-analysis/credit-analysis.module').then((m) => m.CreditAnalysisModule),
    canActivate: [authGuard, roleGuard, tenantOptionGuard],
    data: {
      requiredTenantOptions: [TenantOptions.CREDIT_NEW_CREDIT_ANALYSIS],
    },
    title: 'Análise de crédito',
  },
  {
    path: 'gestao-de-proposta',
    loadChildren: () => import('./proposal-analysis/proposal-analysis.module').then((m) => m.ProposalAnalysisModule),
    canActivate: [authGuard, roleGuard],
    data: { appEnabled: true, requiredRoles: PROPOSAL_ALLOWED_ROLES, requiredRolesMode: 'any' },
    title: 'Gestão de propostas',
  },
  {
    path: 'clientes',
    redirectTo: 'cliente',
    pathMatch: 'full',
  },
  {
    path: 'cliente',
    loadChildren: () => import('./customers/customers.module').then((m) => m.CustomersModule),
    canActivate: [authGuard, roleGuard],
    title: 'Cadastro',
    data: { appEnabled: true },
  },
  {
    path: 'formalizacao',
    loadChildren: () => import('./formalization/formalization.module').then((m) => m.FormalizationModule),
    canActivate: [authGuard, roleGuard],
    title: 'Formalização',
  },
  {
    path: 'laudos',
    loadChildren: () => import('./appraisal/appraisal.routes').then((m) => m.APPRAISAL_ROUTES),
    canActivate: [authGuard, roleGuard],
    data: {
      requiredRoles: ['OPERATION_REQUESTER'],
      requiredRolesMode: 'any',
    },
    title: 'Laudo técnico',
  },
  {
    path: 'logout',
    loadComponent: () => import('./auth/pages/logout/logout.component').then((m) => m.LogoutComponent),
    data: { appEnabled: true },
  },
  {
    path: 'export-pdf',
    loadChildren: () => import('./export-pdf/export-pdf.module').then((m) => m.ExportPdfModule),
    canActivate: [authGuard, roleGuard],
    title: 'Exportação de PDF',
  },
  {
    path: 'sessao-encerrada',
    loadComponent: () =>
      import('./shared/app-ui/pages/mobile-session-ended/mobile-session-ended.component').then(
        (m) => m.MobileSessionEndedComponent,
      ),
    canActivate: [appOnlyGuard],
    title: 'Sessão encerrada',
    data: { appEnabled: true },
  },
  {
    path: 'nao-autorizado',
    loadComponent: () => import('./not-allowed/not-allowed-entry.component').then((m) => m.NotAllowedEntryComponent),
    title: 'Não autorizado',
    data: { appEnabled: true },
  },
  {
    path: 'consumo',
    loadComponent: () =>
      import('./consumption/pages/consumption-page/consumption-page.component').then((m) => m.ConsumptionPageComponent),
    canActivate: [authGuard],
    title: 'Consumo',
  },
  {
    path: 'configuracoes/chaves-de-api/sessao',
    loadComponent: () =>
      import('./settings/pages/api-keys/api-keys-share-session/api-keys-share-session.component').then(
        (m) => m.ApiKeysShareSessionComponent,
      ),
    title: 'Sessão Temporária de API Keys',
  },
  {
    path: 'configuracoes',
    loadChildren: () => import('./settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
    canActivate: [authGuard, roleGuard],
    title: 'Configurações',
  },
  {
    path: 'amplitude/dicionario',
    loadComponent: () =>
      import('./shared/analytics/dictionary/analytics-dictionary.component').then(
        (m) => m.AnalyticsDictionaryComponent,
      ),
    canActivate: [authGuard, internalUserGuard],
    title: 'Dicionário de Eventos',
  },
];

const MAINTENANCE_ROUTES: Routes = [
  {
    path: 'manutencao',
    loadComponent: () =>
      import('./shared/components/maintenance-page/maintenance-page.component').then((m) => m.MaintenancePageComponent),
    title: 'Manutenção programada',
  },
  {
    path: '**',
    redirectTo: 'manutencao',
  },
];

export function buildRoutes(maintenanceEnabled: boolean): Routes {
  return maintenanceEnabled ? MAINTENANCE_ROUTES : withAppGuard(APP_ROUTES);
}

export const routes: Routes = buildRoutes(MAINTENANCE_ENABLED);
